/**
 * MultiSelect Component
 *
 * A dropdown that allows selecting multiple options with checkboxes.
 * Follows UI/UX guidelines for form inputs and accessibility.
 */

import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '../Badge/Badge';
import { __resolve } from '@/lib/i18n';

export interface MultiSelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  maxHeight?: string;
  /** L1 (AC-K-L1): forward data-testid to the root element for reliable smoke tests */
  'data-testid'?: string;
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  label,
  placeholder = __resolve('ui::Select options...'),
  error,
  disabled = false,
  required = false,
  className,
  maxHeight = 'max-h-60',
  'data-testid': dataTestId,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleToggle = (optionValue: string) => {
    if (disabled) return;

    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    onChange(value.filter((v) => v !== optionValue));
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    onChange([]);
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  return (
    <div className={cn('w-full', className)} ref={containerRef} data-testid={dataTestId}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {label}
          {required && <span className="text-error-600 ml-1">*</span>}
        </label>
      )}

      {/* Trigger Container */}
      <div
        ref={buttonRef as any}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            !disabled && setIsOpen(!isOpen);
          }
        }}
        className={cn(
          'relative w-full min-h-[40px] px-3 py-2 text-left',
          'bg-background border rounded-md',
          'transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          error
            ? 'border-error-300 focus:ring-error-500'
            : 'border-border hover:border-muted-foreground/50',
          disabled ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'cursor-pointer',
          'flex items-center justify-between gap-2'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? 'multiselect-label' : undefined}
        aria-disabled={disabled}
      >
        <div className="flex-1 flex items-center flex-wrap gap-1.5 min-h-[24px]">
          {selectedOptions.length === 0 ? (
            <span className="text-muted-foreground text-sm">{placeholder}</span>
          ) : (
            selectedOptions.map((option) => (
              <Badge
                key={option.value}
                variant="secondary"
                className="flex items-center gap-1 py-0.5"
              >
                <span className="text-xs">{option.label}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => handleRemove(option.value, e)}
                    className="hover:bg-muted rounded-full p-0.5 transition-colors"
                    aria-label={__resolve('ui::Remove {{label}}', { label: option.label })}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </Badge>
            ))
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {selectedOptions.length > 0 && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="hover:bg-muted rounded p-0.5 transition-colors"
              aria-label={__resolve('ui::Clear all selections')}
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform',
              isOpen && 'transform rotate-180'
            )}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full',
            'bg-popover border border-border rounded-md shadow-lg',
            maxHeight,
            'overflow-auto'
          )}
          role="listbox"
          aria-multiselectable="true"
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              {__resolve('ui::No options available')}
            </div>
          ) : (
            <div className="py-1">
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                const isDisabled = option.disabled || false;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => !isDisabled && handleToggle(option.value)}
                    disabled={isDisabled}
                    className={cn(
                      'w-full px-3 py-2 text-left',
                      'flex items-start gap-3',
                      'transition-colors duration-150',
                      'focus:outline-none focus:bg-accent',
                      isDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:bg-accent cursor-pointer',
                      isSelected && 'bg-accent/50'
                    )}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {/* Checkbox indicator */}
                    <div
                      className={cn(
                        'flex-shrink-0 w-4 h-4 mt-0.5 border rounded',
                        'flex items-center justify-center',
                        'transition-colors duration-150',
                        isSelected
                          ? 'bg-primary-600 border-primary-600'
                          : 'bg-background border-border'
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    {/* Option content */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-popover-foreground">
                        {option.label}
                      </div>
                      {option.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {option.description}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1.5 text-sm text-error-600" role="alert">
          {error}
        </p>
      )}

      {/* Helper text for screen readers */}
      {value.length > 0 && (
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {__resolve(value.length === 1 ? 'ui::{{count}} option selected' : 'ui::{{count}} options selected', { count: value.length })}
        </div>
      )}
    </div>
  );
}
